import { ReactNode, useCallback, useMemo, useState } from 'react';
import { useFormspark } from '@formspark/use-formspark';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Page from './components/AuthPage';
import { BooleanSelector } from '@/components/common/BooleanSelector';
import { CountrySelector } from '@/components/common/CountrySelector';
import { LoadingIndicator } from '@/components/common/LoadingIndicator';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';

const DEFAULT_ANSWERS = {
  basics: {
    companyAddress: null,
    companyName: null,
    country: null,
    email: null,
    EORINumber: null,
    taxNumber: null,
  },
  goods: {
    detailedDescription: null,
    shortDescription: null,
    specialAuthorisations: null,
    specialAuthorisationsDescription: null,
    TARICCode: null,
  },
  hungarianCustomers: {
    aggregatedTaxCurrentAccount: null,
    navMasterData: null,
  },
  signatories: {
    emailAddress: null,
    IDCard: null,
    nameOfSignatory: null,
    position: null,
    sampleSignature: null,
  },
  transport: {
    expectedVolume: null,
    originCountry: null,
    plannedRoute: null,
    valueOfShipment: null,
  },
};

const InputContainer = ({
  htmlFor,
  label,
  children,
}: {
  children: React.ReactNode;
  htmlFor?: string;
  label: string | ReactNode;
}) => {
  return (
    <div className="flex flex-col w-full gap-1.5 mb-4 px-px">
      <Label className="w-full" htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
    </div>
  );
};

function PreRegistrationPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [submit, submitting] = useFormspark({
    formId: import.meta.env.VITE_FORMSPARK_PRE_REGISTRATION_FORM_ID,
  });

  const [formData, setFormData] =
    useState<Record<string, Record<string, string | null>>>(DEFAULT_ANSWERS);
  const [loading] = useState(false);

  const isEnabled = useMemo(
    () =>
      Object.values(formData.basics).every(
        (value) => value !== null && value !== ''
      ),
    [formData.basics]
  );

  const updateFormData = (value: string, key: string, parentKey: string) => {
    setFormData({
      ...formData,
      [parentKey]: {
        ...(formData[parentKey] as Record<string, string>),
        [key]: value,
      },
    });
  };

  const handleSend = useCallback(async () => {
    console.log('Send form');
    // TODO: send form data to backend

    submit(formData);
    toast.success(t('preRegistrationFormSent'));
    navigate('/login');
  }, [formData, navigate, submit, t]);

  return (
    <Page link={{ title: t('login'), to: '/login' }}>
      <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-96">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('preRegistrationForm')}
          </h1>
          {/* <p className="text-sm text-muted-foreground">{t('enterYourEmail')}</p> */}
        </div>
        <Accordion collapsible className="w-full" type="single">
          <AccordionItem value="item-1">
            <AccordionTrigger className="w-full">
              {t('contacts')}
            </AccordionTrigger>
            <AccordionContent className="pt-2">
              <InputContainer htmlFor="email" label={t('email')}>
                <Input
                  placeholder={t('email')}
                  type="email"
                  value={formData.basics.email ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'email', 'basics')
                  }
                />
              </InputContainer>

              <InputContainer label={t('basics.companyName')}>
                <Input
                  placeholder={t('basics.companyName')}
                  value={formData.basics.companyName ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'companyName', 'basics')
                  }
                />
              </InputContainer>

              <InputContainer label={t('basics.companyAddress')}>
                <Input
                  placeholder={t('basics.companyAddress')}
                  value={formData.basics.companyAddress ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'companyAddress', 'basics')
                  }
                />
              </InputContainer>

              <InputContainer label={t('country')}>
                <CountrySelector
                  value={formData.basics.country ?? ''}
                  onChange={(value) =>
                    updateFormData(value, 'country', 'basics')
                  }
                />
              </InputContainer>

              <InputContainer label={t('basics.taxNumber')}>
                <Input
                  placeholder={t('basics.taxNumber')}
                  value={formData.basics.taxNumber ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'taxNumber', 'basics')
                  }
                />
              </InputContainer>

              <InputContainer label={t('basics.EORINumber')}>
                <Input
                  placeholder={t('basics.EORINumber')}
                  value={formData.basics.EORINumber ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'EORINumber', 'basics')
                  }
                />
              </InputContainer>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-2">
            <AccordionTrigger>{t('descriptionOfGoods')}</AccordionTrigger>
            <AccordionContent>
              <InputContainer label={t('goods.shortDescription')}>
                <Input
                  placeholder=""
                  value={formData.goods.shortDescription ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'shortDescription',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer label={t('goods.detailedDescription')}>
                <Textarea
                  placeholder=""
                  value={formData.goods.detailedDescription ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'detailedDescription',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer label={t('goods.TARICCode')}>
                <Input
                  placeholder=""
                  value={formData.goods.TARICCode ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'TARICCode',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer
                htmlFor="specialAuthorisations"
                label={t('goods.specialAuthorisations')}
              >
                <BooleanSelector
                  value={formData.goods.specialAuthorisations ?? ''}
                  onChange={(value) =>
                    updateFormData(
                      value,
                      'specialAuthorisations',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer
                htmlFor="specialAuthorisationsDescription"
                label={t('goods.specialAuthorisationsDescription')}
              >
                <BooleanSelector
                  value={formData.goods.specialAuthorisationsDescription ?? ''}
                  onChange={(value) =>
                    updateFormData(
                      value,
                      'specialAuthorisationsDescription',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-3">
            <AccordionTrigger>{t('descriptionOfTransport')}</AccordionTrigger>
            <AccordionContent className="pt-2">
              <InputContainer
                htmlFor="originCountry"
                label={t('transport.originCountry')}
              >
                <CountrySelector
                  value={formData.transport.originCountry ?? ''}
                  onChange={(value) =>
                    updateFormData(value, 'originCountry', 'goodsAndTransport')
                  }
                />
              </InputContainer>

              <InputContainer
                htmlFor="plannedRoute"
                label={t('transport.plannedRoute')}
              >
                <Input
                  placeholder=""
                  value={formData.transport.plannedRoute ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'plannedRoute',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer
                htmlFor="valueOfShipment"
                label={t('transport.valueOfShipment')}
              >
                <Input
                  placeholder=""
                  value={formData.transport.valueOfShipment ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'valueOfShipment',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>

              <InputContainer
                htmlFor="expectedVolume"
                label={t('transport.expectedVolume')}
              >
                <Input
                  placeholder=""
                  value={formData.transport.expectedVolume ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'expectedVolume',
                      'goodsAndTransport'
                    )
                  }
                />
              </InputContainer>
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-4">
            <AccordionTrigger>{t('signatory')}</AccordionTrigger>
            <AccordionContent className="pt-2">
              <InputContainer label={t('signatories.nameOfSignatory')}>
                <Input
                  placeholder=""
                  value={formData.signatories.nameOfSignatory ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'nameOfSignatory',
                      'signatories'
                    )
                  }
                />
              </InputContainer>
              <InputContainer label={t('signatories.position')}>
                <Input
                  placeholder=""
                  value={formData.signatories.position ?? ''}
                  onChange={(e) =>
                    updateFormData(e.target.value, 'position', 'signatories')
                  }
                />
              </InputContainer>
              <InputContainer label={t('signatories.emailAddress')}>
                <Input
                  placeholder=""
                  value={formData.signatories.emailAddress ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'emailAddress',
                      'signatories'
                    )
                  }
                />
              </InputContainer>
              {/* TODO: change to image upload or use e-signature */}
              {/* Sample Signature */}
              {/* TODO: change to image upload */}
              {/* ID CARD */}
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-5">
            <AccordionTrigger>
              {t('customersRegisteredInHungary')}
            </AccordionTrigger>
            <AccordionContent className="pt-2">
              <InputContainer
                label={
                  <div className="flex gap-1">
                    <span>{t('hungarianCustomers.navMasterData')}</span>
                    <a
                      className="underline text-blue-600"
                      href="https://ebev.nav.gov.hu/"
                      target="_blank"
                    >
                      (https://ebev.nav.gov.hu/)
                    </a>
                  </div>
                }
              >
                <Input
                  placeholder=""
                  value={formData.hungarianCustomers.navMasterData ?? ''}
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'navMasterData',
                      'hungarianCustomers'
                    )
                  }
                />
              </InputContainer>
              <InputContainer
                label={t('hungarianCustomers.aggregatedTaxCurrentAccount')}
              >
                <Input
                  placeholder=""
                  value={
                    formData.hungarianCustomers.aggregatedTaxCurrentAccount ??
                    ''
                  }
                  onChange={(e) =>
                    updateFormData(
                      e.target.value,
                      'aggregatedTaxCurrentAccount',
                      'hungarianCustomers'
                    )
                  }
                />
              </InputContainer>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <Button
          disabled={!isEnabled || loading || submitting}
          title={t('send')}
          type="button"
          variant="default"
          onClick={handleSend}
        >
          {loading || (submitting && <LoadingIndicator className="mr-2" />)}
          {t('send')}
        </Button>
      </div>
    </Page>
  );
}

export const Component = PreRegistrationPage;
